import { Button, Popover, Tag, Tooltip } from 'antd';
import clsx from 'clsx';
import type { Key, MouseEvent } from 'react';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FileLimitationEnum } from '../../../enums/FileLimitationEnum';
import { FileMenuActionEnum } from '../../../enums/FileMenuActionEnum';
import { useColorScheme } from '../../../hooks/useColorScheme/useColorScheme';
import { useTags } from '../../../hooks/useTags';
import type File from '../../../interfaces/File';
import { getVisibleCatalogTagsForFile } from '../../../lib/fileVisibleTags/fileVisibleTags';
import { getTagPresentationStyle } from '../../../utils/colorsUtilis';
import { hasFileLimitation } from '../../../utils/utils';
import TruncatedTooltip from '../../Common/TruncatedTooltip';
import { Icon } from '../../Core/Icon';

interface FilesTableTagsCellProps {
  file: File;
  handleFileMenuAction: (filesIds: number[], action: FileMenuActionEnum) => void;
  setSelectedTableKeys: (keys: Key[]) => void;
}

/**
 * Dedicated tags column cell: shows the first visible tag (label truncated) and a
 * `+N` popover for the rest — same pattern as the pre-responsive table layout.
 * Tag click opens FileTagsForm when Tags menu action is available for the file.
 */
const FilesTableTagsCell: React.FC<FilesTableTagsCellProps> = ({
  file,
  handleFileMenuAction,
  setSelectedTableKeys,
}) => {
  const { tags } = useTags();
  const { t } = useTranslation();
  const { resolvedColorScheme } = useColorScheme();
  const isDarkMode = resolvedColorScheme === 'dark';

  const visibleFileTags = useMemo(() => getVisibleCatalogTagsForFile(file, tags), [file, tags]);

  const firstTag = visibleFileTags?.[0];
  const overflowTags = useMemo(() => visibleFileTags?.slice(1) ?? [], [visibleFileTags]);
  const usedForVisibilityLabel = t('common.tagIsUsedForFileVisibility');

  // Same gate as file menu Tags item / actions-cell Tags button.
  const canEditTags = useMemo(() => {
    return (
      !hasFileLimitation(file.fileLimitations, FileLimitationEnum.DenyFileTags) && tags.length > 0
    );
  }, [file.fileLimitations, tags.length]);

  const openTagsModal = useCallback(
    (event: MouseEvent) => {
      if (!canEditTags) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      setSelectedTableKeys([file.id]);
      handleFileMenuAction([file.id], FileMenuActionEnum.Tags);
    },
    [canEditTags, file.id, handleFileMenuAction, setSelectedTableKeys],
  );

  if (firstTag == null) {
    return null;
  }

  return (
    <div data-testid="files-table-tags-cell" className="flex min-w-0 flex-row items-center gap-1">
      <Tag
        key={firstTag.id}
        className={clsx('m-0! max-w-full min-w-0', canEditTags && 'cursor-pointer')}
        style={getTagPresentationStyle(firstTag.color, { darkMode: isDarkMode })}
        onClick={openTagsModal}
      >
        <div className="flex max-w-full min-w-0 flex-row items-center gap-1">
          <span className="min-w-0 flex-1 overflow-hidden">
            <TruncatedTooltip className="min-w-0">{firstTag.title}</TruncatedTooltip>
          </span>
          <div className="flex shrink-0 flex-row items-center gap-0.5">
            {firstTag.isUsedForFileVisibility ? (
              <Tooltip title={usedForVisibilityLabel}>
                <div>
                  <Icon name="Share2Icon" className="text-gray-dark h-3 w-3" />
                </div>
              </Tooltip>
            ) : null}
            {firstTag.publicTag ? (
              <Icon name="BuildingIcon" className="text-gray-dark h-3 w-3" />
            ) : null}
          </div>
        </div>
      </Tag>

      {overflowTags.length > 0 && (
        <Popover
          content={
            <div
              className="flex w-full flex-col gap-2"
              onClick={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.stopPropagation()}
            >
              {overflowTags.map((tag) => (
                <Tag
                  key={tag.id}
                  className={clsx(canEditTags && 'cursor-pointer')}
                  style={getTagPresentationStyle(tag.color, { darkMode: isDarkMode })}
                  onClick={openTagsModal}
                >
                  <div className="flex w-32 flex-row items-center justify-center lg:justify-between">
                    <div />
                    <span className="max-w-28">
                      <TruncatedTooltip>{tag.title}</TruncatedTooltip>
                    </span>
                    <div className="flex flex-row items-center gap-1">
                      {tag.isUsedForFileVisibility ? (
                        <Tooltip title={usedForVisibilityLabel}>
                          <div>
                            <Icon name="Share2Icon" className="text-gray-dark h-3 w-3" />
                          </div>
                        </Tooltip>
                      ) : (
                        <div className="w-1" />
                      )}
                      {tag.publicTag ? (
                        <Icon name="BuildingIcon" className="text-gray-dark h-3 w-3" />
                      ) : (
                        <div className="w-1" />
                      )}
                    </div>
                  </div>
                </Tag>
              ))}
            </div>
          }
          trigger={['hover', 'click']}
        >
          <Button
            size="small"
            shape="circle"
            className="flex! h-5! w-5! min-w-5! shrink-0! items-center! justify-center! p-0! text-[10px]! leading-none!"
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            +{overflowTags.length}
          </Button>
        </Popover>
      )}
    </div>
  );
};

export default FilesTableTagsCell;
